/**
 * @fileoverview Input/output schemas for world-event-details-generator-v3.
 *
 * This agent generates complete details for a world event based on a selected direction.
 * Supports multimodal input with optional reference images.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import { WorldEventAttributesV3Schema } from '@/domains/world-building/schemas';
import { WorldEventDetailsIdeaSchema } from '../world-event-details-ideation-v3/schema';

export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.storyContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.loreContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.activeWorldEvent.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.characters.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.userMessage.schema.shape,
  selectedIdea: WorldEventDetailsIdeaSchema.describe('The creative direction selected by the user.'),
  imageDataUris: z.array(z.string()).max(5).optional().describe(
    "Optional array of image data URIs (base64 encoded) as visual references for the event."
  ),
});

export type Input = z.infer<typeof InputSchema>;

export const OutputSchema = z.object({
  isValidImage: z.boolean().describe(
    "True if no images were provided OR if images are valid visual references. False if image validation failed."
  ),
  validationError: z.string().optional().describe(
    "Explanation of why the image is invalid, if isValidImage is false."
  ),
  attributes: WorldEventAttributesV3Schema.optional().describe(
    "The generated event attributes. Only present if isValidImage is true."
  ),
});

export type Output = z.infer<typeof OutputSchema>;
